import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { http } from "../lib/api";
import { useSeo } from "../hooks/useSeo";
import { Reveal, Btn, Eyebrow } from "../components/Bits";

export default function Article() {
  const { slug } = useParams();
  const [article, setArticle] = useState(null);
  const [others, setOthers] = useState([]);
  const [missing, setMissing] = useState(false);

  useEffect(() => {
    setArticle(null);
    setMissing(false);
    http
      .get(`/content/articles/${slug}`)
      .then((r) => setArticle(r.data))
      .catch(() => setMissing(true));
    http.get("/content/articles").then((r) => setOthers(r.data)).catch(() => {});
  }, [slug]);

  useSeo({
    title: article ? `${article.title} | MAKO Mats` : "MAKO Mats Resources",
    description: article?.excerpt,
  });

  if (missing)
    return (
      <div className="mx-auto max-w-[1400px] px-6 py-32 lg:px-10">
        <h1 className="font-display text-3xl font-bold uppercase text-[#111111]">
          Article not found
        </h1>
        <div className="mt-10">
          <Btn to="/resources">Back to resources</Btn>
        </div>
      </div>
    );

  if (!article) return <div className="min-h-[60vh]" data-testid="article-loading" />;

  const related = others.filter((o) => o.slug !== article.slug).slice(0, 3);

  return (
    <article data-testid={`article-${article.slug}`}>
      <header className="border-b border-[#111111]/10 bg-white">
        <div className="mako-grid-bg">
          <div className="mx-auto max-w-[900px] px-6 py-16 lg:px-10 lg:py-24">
            <Eyebrow>{article.category}</Eyebrow>
            <h1 className="mt-7 font-display text-3xl font-bold leading-[1.05] tracking-tight text-[#111111] sm:text-4xl lg:text-[3rem]">
              {article.title}
            </h1>
            <p className="mt-7 text-sm leading-relaxed text-[#3B3B3B] sm:text-base">
              {article.excerpt}
            </p>
            <p className="mako-label mt-8 text-[#7A7F85]">{article.read_time}</p>
          </div>
        </div>
      </header>

      <div className="mx-auto max-w-[900px] px-6 py-14 lg:px-10 lg:py-20">
        <div className="aspect-[16/9] overflow-hidden bg-[#F5F5F2]">
          <img src={article.image} alt={article.title} className="h-full w-full object-cover" />
        </div>

        <div className="mt-14 space-y-12">
          {(article.body || []).map((block, i) => (
            <Reveal key={block.h} delay={i * 0.04}>
              <section>
                <h2 className="font-display text-xl font-bold uppercase tracking-tight text-[#111111] sm:text-2xl">
                  {block.h}
                </h2>
                <div className="mt-4 h-[2px] w-16 bg-[#0F4C81]" />
                <p className="mt-6 text-sm leading-[1.85] text-[#3B3B3B] sm:text-base">
                  {block.p}
                </p>
              </section>
            </Reveal>
          ))}
        </div>

        <div className="mt-16 border-t border-[#111111]/10 pt-12">
          <p className="mako-label text-[#0F4C81]">Next step</p>
          <p className="mt-5 font-display text-xl font-bold uppercase leading-tight text-[#111111] sm:text-2xl">
            Talk to MAKO about your room.
          </p>
          <div className="mt-8">
            <Btn to="/contact" data-testid="article-cta">
              Request a quote
            </Btn>
          </div>
        </div>
      </div>

      <section className="border-t border-[#111111]/10 bg-[#F5F5F2] py-16 lg:py-24">
        <div className="mx-auto max-w-[1400px] px-6 lg:px-10">
          <p className="mako-label text-[#7A7F85]">Continue reading</p>
          <div className="mt-10 grid gap-8 sm:grid-cols-3">
            {related.map((r) => (
              <Link
                key={r.slug}
                to={`/resources/${r.slug}`}
                data-testid={`related-article-${r.slug}`}
                className="group"
              >
                <div className="aspect-[16/10] overflow-hidden bg-white">
                  <img
                    src={r.image}
                    alt={r.title}
                    loading="lazy"
                    className="h-full w-full object-cover transition-transform duration-[900ms] group-hover:scale-[1.04]"
                  />
                </div>
                <p className="mako-label mt-5 text-[#0F4C81]">{r.category}</p>
                <p className="mt-3 font-display text-base font-bold leading-snug text-[#111111] transition-colors duration-300 group-hover:text-[#0F4C81]">
                  {r.title}
                </p>
              </Link>
            ))}
          </div>
        </div>
      </section>
    </article>
  );
}
